import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, Sparkles, ArrowRight } from 'lucide-react';

const PLANS = [
  {
    name: 'Starter',
    description: 'Pour les petites équipes qui démarrent',
    price: '0',
    period: 'Gratuit',
    features: [
      'Jusqu\'à 3 utilisateurs',
      'Projets illimités',
      'Vues Kanban & Liste',
      'Tâches & sous-tâches',
      'Support email'
    ],
    cta: 'Commencer gratuitement',
    popular: false
  },
  {
    name: 'Pro',
    description: 'Pour les équipes qui veulent piloter',
    price: '12',
    period: '/utilisateur/mois',
    features: [
      'Utilisateurs illimités',
      'Toutes les vues (Gantt, Calendrier...)',
      'Charge & capacité équipe',
      'Feuilles de temps',
      'Budget projet',
      'Intégrations',
      'Support prioritaire'
    ],
    cta: 'Essai gratuit 14 jours',
    popular: true
  },
  {
    name: 'Business',
    description: 'Pour les workspaces exigeants',
    price: '24',
    period: '/utilisateur/mois',
    features: [
      'Tout le plan Pro',
      'SSO / SAML',
      'Rôles & permissions avancés',
      'Audit logs',
      'API complète',
      'SLA garanti',
      'Account manager dédié'
    ],
    cta: 'Contacter les ventes',
    popular: false
  }
];

export function PricingSection() {
  return (
    <section id="pricing" className="py-24 md:py-32 bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Tarifs
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Simple et transparent
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Choisissez le plan adapté à votre équipe. 
            Évoluez quand vous en avez besoin.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative p-6 md:p-8 rounded-2xl border transition-all duration-300 ${
                plan.popular
                  ? 'bg-gradient-to-b from-indigo-50 to-violet-50 dark:from-indigo-950/50 dark:to-violet-950/50 border-indigo-300 dark:border-indigo-700 shadow-xl shadow-indigo-500/10 scale-105 z-10'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-lg'
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-medium shadow-lg">
                    Recommandé
                  </span>
                </div>
              )}

              {/* Plan name */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {plan.description}
                </p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">
                    {plan.price}€
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {plan.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className={`h-5 w-5 mt-0.5 shrink-0 ${plan.popular ? 'text-indigo-600 dark:text-indigo-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
                    <span className="text-slate-700 dark:text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                className={`w-full ${
                  plan.popular
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/25'
                    : ''
                }`}
                variant={plan.popular ? 'default' : 'outline'}
                size="lg"
                asChild
              >
                <a href="https://app.lapyno.com/auth?mode=signup">
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          ))}
        </div>

        {/* Additional info */}
        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-8">
          Tous les prix sont HT. Paiement mensuel ou annuel (-20%).
        </p>
      </div>
    </section>
  );
}
